import { useCallback } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  METRICS_SECTIONS,
  isMetricsSectionSlug,
  metricsSectionPath,
  type MetricsSectionParent,
} from "../routes/slugs";

/**
 * Sección interna de una pestaña de Métricas (Liquidación, PSI+) como estado de la URL:
 * `/profile/metricas/<parent>` es el default y `/profile/metricas/<parent>/<seccion>` las demás.
 *
 * Reemplaza al `useState` local que tenían esas pestañas: el JSX no cambia (devuelve la misma dupla
 * `[seccion, setSeccion]`), pero ahora la sección tiene su propia URL (link directo, botón atrás, y una pantalla propia
 * en Métricas de Actividad). Un slug desconocido cae al default sin redirigir, igual que las demás tabs.
 *
 * `setSection` navega con `replace` (mismo criterio que `ProfileMetricsTab`: cambiar de pestaña no apila historial) y
 * CONSERVA el query string (ej. `?periodo=` de los links de los mails de liquidación).
 */
export function useMetricsSection<T extends string>(parent: MetricsSectionParent): [T, (section: T) => void] {
  const navigate = useNavigate();
  const { search } = useLocation();
  const { section: sectionSlug } = useParams<{ section?: string }>();

  const section = (sectionSlug && isMetricsSectionSlug(parent, sectionSlug)
    ? sectionSlug
    : METRICS_SECTIONS[parent].default) as T;

  const setSection = useCallback(
    (next: T) => {
      navigate(`${metricsSectionPath(parent, next)}${search}`, { replace: true });
    },
    [navigate, parent, search],
  );

  return [section, setSection];
}
